/**
 * AnalysisTemplates — /agents/analysis-templates. The prompts that drive intel-source analysis.
 * Demo page: `/tables/data-tables` (search, filter pills, table, empty state). Template: ListPage, the same shape as
 * `pages/tasks/TasksPage.tsx`.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plus, Sparkles } from 'lucide-react';
import { listAnalysisTemplatesOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { IntelAnalysisTemplate } from '../../generated/api/types.gen.js';
import { Can } from '../../components/auth/Can.js';
import { ListPage } from '../../components/page/ListPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Badge, Button, Callout, DataTable, EmptyState, FilterPills, SearchBar, Toolbar } from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { PAGE_HELP } from '../../help';

type PillId = 'all' | 'custom' | 'system';

const NEW_TEMPLATE_PATH = '/agents/analysis-templates/new';

function label(value: string | null | undefined): string {
  return (value ?? '').replace(/_/g, ' ');
}

/** `nameById` resolves a parent template's name; ids are never shown. */
function columns(nameById: ReadonlyMap<number, string>): DataTableColumn<IntelAnalysisTemplate>[] {
  return [
    {
      key: 'name',
      header: 'Template',
      width: '36%',
      sortable: true,
      sortValue: (t) => (t.name ?? '').toLowerCase(),
      render: (t) => (
        <div className="min-w-0">
          <Link
            to={`/agents/analysis-templates/${t.id}`}
            className="font-semibold text-heading hover:text-accent-text"
            onClick={(e) => e.stopPropagation()}
          >
            {t.name ?? 'Untitled template'}
          </Link>
          <p className="mt-0.5 line-clamp-1 text-xs text-muted">{t.description || 'No description'}</p>
        </div>
      ),
    },
    {
      key: 'platform',
      header: 'Platform',
      sortable: true,
      sortValue: (t) => t.platform ?? '',
      render: (t) => <span className="capitalize text-muted">{label(t.platform)}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      sortValue: (t) => t.category ?? '',
      render: (t) => (
        <Badge variant="neutral" size="sm">
          {label(t.category)}
        </Badge>
      ),
    },
    {
      key: 'source',
      header: 'Type',
      render: (t) => (
        <Badge variant={t.source === 'system' ? 'info' : 'accent'} size="sm">
          {t.source === 'system' ? 'System default' : 'Custom'}
        </Badge>
      ),
    },
    {
      key: 'extends',
      header: 'Extends',
      render: (t) =>
        t.extendsId ? (
          <span className="text-muted">{nameById.get(Number(t.extendsId)) ?? 'A system template'}</span>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
  ];
}

export const AnalysisTemplatesPage: React.FC = () => {
  const help = PAGE_HELP['analysis-templates'];
  const { data, isLoading, isError, refetch } = useQuery(listAnalysisTemplatesOptions());
  const templates = useMemo(() => data?.data ?? [], [data]);
  const nameById = new Map(templates.map((t) => [Number(t.id), t.name ?? 'Untitled template']));

  const [search, setSearch] = useState('');
  const [pill, setPill] = useState<PillId>('all');

  const pillOptions = [
    { id: 'all', label: 'All', count: templates.length },
    { id: 'custom', label: 'Custom', count: templates.filter((t) => t.source === 'org').length },
    { id: 'system', label: 'System defaults', count: templates.filter((t) => t.source === 'system').length },
  ];

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => pill === 'all' || (pill === 'system' ? t.source === 'system' : t.source === 'org'))
      .filter(
        (t) =>
          !q ||
          (t.name ?? '').toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q) ||
          label(t.platform).includes(q),
      );
  }, [templates, pill, search]);

  const newTemplate = (
    <Can policy="CanManageAgents">
      <Link to={NEW_TEMPLATE_PATH} className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        New template
      </Link>
    </Can>
  );

  return (
    <ListPage
      title="Analysis templates"
      subtitle="Reusable prompts that tell an agent what to pull out of each item a source collects."
      helpContent={help}
      actions={newTemplate}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load analysis templates"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search templates…" />}
          pills={
            <FilterPills
              aria-label="Template type"
              options={pillOptions}
              value={pill}
              onChange={(id) => setPill(id as PillId)}
            />
          }
        />
      }
    >
      {!isError && (
        <DataTable
          columns={columns(nameById)}
          rows={visible}
          rowKey={(t) => t.id}
          loading={isLoading}
          aria-label="Analysis templates"
          empty={
            <EmptyState
              icon={Sparkles}
              title={templates.length === 0 ? help.emptyState.title : 'No templates match'}
              description={
                templates.length === 0 ? help.emptyState.description : 'Nothing matches the current search and filter.'
              }
              learnItems={templates.length === 0 ? help.emptyState.learnItems : undefined}
              action={templates.length === 0 ? newTemplate : undefined}
            />
          }
        />
      )}
    </ListPage>
  );
};
